import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Loader2, RefreshCw, AlertTriangle, ChevronRight,
  Check, CheckCircle2, Clock, TrendingUp, Target, Sparkles, ArrowRight,
} from 'lucide-react';
import { assessOpportunity, listForecastAssessments, MavisApiError, updateOpportunity } from '../../lib/mavisApi';
import { captureEdgeFunctionError } from '../../lib/sentry';
import type {
  MavisOpportunity,
  MavisOpportunityQualification,
  MavisForecastAssessmentRow,
} from '../../types/mavis';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  opportunity: MavisOpportunity;
  qualification: MavisOpportunityQualification | null;
  onForecastApplied: (patch: Record<string, unknown>) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function labelColor(label: string): string {
  switch (label?.toLowerCase()) {
    case 'commit ready':  return 'text-forest-700 bg-forest-50 border-forest-200';
    case 'high':          return 'text-blue-700 bg-blue-50 border-blue-200';
    case 'medium':        return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'commit risk':   return 'text-orange-700 bg-orange-50 border-orange-200';
    case 'low':           return 'text-red-600 bg-red-50 border-red-200';
    default:              return 'text-app-muted bg-app-canvas border-app-line';
  }
}

function severityColor(s: string): string {
  if (s === 'High')   return 'text-red-600 bg-red-50';
  if (s === 'Medium') return 'text-amber-600 bg-amber-50';
  return 'text-app-muted bg-app-canvas';
}

function priorityColor(p: string): string {
  if (p === 'High')   return 'bg-red-500';
  if (p === 'Medium') return 'bg-amber-400';
  return 'bg-app-line';
}

function ScoreDial({ value, label }: { value: number | null; label: string }) {
  const pct = value !== null ? Math.max(0, Math.min(100, value)) : null;
  const color = pct === null ? 'text-app-muted'
    : pct >= 70 ? 'text-forest-600'
    : pct >= 40 ? 'text-amber-600'
    : 'text-red-500';
  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-3xl font-bold ${color}`}>
        {pct !== null ? pct : '—'}
      </span>
      <span className="text-[11px] text-app-muted text-center">{label}</span>
    </div>
  );
}

function calcQualCompleteness(qual: MavisOpportunityQualification | null): number {
  if (!qual) return 0;
  let filled = 0; let total = 0;
  if (qual.meddpicc) {
    const m = qual.meddpicc as Record<string, { value: string }>;
    for (const f of Object.values(m)) { total++; if (f.value?.trim()) filled++; }
  } else { total += 8; }
  if (qual.now_to_value) {
    const n = qual.now_to_value as Record<string, { value: string }>;
    for (const f of Object.values(n)) { total++; if (f.value?.trim()) filled++; }
  } else { total += 7; }
  if (qual.three_whys) {
    const w = qual.three_whys as { why_change: string; why_now: string; why_us: string };
    total += 3;
    if (w.why_change?.trim()) filled++;
    if (w.why_now?.trim()) filled++;
    if (w.why_us?.trim()) filled++;
  } else { total += 3; }
  return total > 0 ? Math.round((filled / total) * 100) : 0;
}

// ── Assessment card ───────────────────────────────────────────────────────────

function AssessmentCard({
  a,
  isLatest,
}: {
  a: MavisForecastAssessmentRow;
  isLatest: boolean;
}) {
  const risks = Array.isArray(a.risks) ? a.risks : [];
  const actions = Array.isArray(a.next_best_actions) ? a.next_best_actions : [];
  const gaps = Array.isArray(a.context_gaps) ? a.context_gaps : [];

  return (
    <div className={`bg-app-surface rounded-2xl border shadow-sm overflow-hidden ${isLatest ? 'border-forest-200' : 'border-app-line'}`}>
      {/* Header */}
      <div className={`px-5 py-4 flex items-start justify-between gap-4 ${isLatest ? 'bg-forest-50/40' : ''}`}>
        <div className="flex items-center gap-3 min-w-0">
          {isLatest && <Sparkles className="w-4 h-4 text-forest-500 flex-shrink-0" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${labelColor(a.forecast_label)}`}>
                {a.forecast_label}
              </span>
              <span className="text-sm font-bold text-app-ink">{a.forecast_percentage}%</span>
              {isLatest && <span className="text-[10px] text-forest-600 font-semibold">LATEST</span>}
            </div>
            <p className="text-[11px] text-app-muted mt-0.5">
              {new Date(a.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
        </div>
        <div className="flex gap-6 flex-shrink-0">
          <ScoreDial value={a.opportunity_strength_score ?? null} label="Deal strength" />
          <ScoreDial value={a.qualification_score ?? null} label="Qual score" />
          <ScoreDial value={a.confidence ?? null} label="Confidence" />
        </div>
      </div>

      {/* Summary */}
      {a.summary && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-sm text-app-ink leading-relaxed">{a.summary}</p>
        </div>
      )}

      {/* Evidence */}
      {a.evidence_summary && (
        <div className="px-5 py-3 border-t border-app-line bg-blue-50/30">
          <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide mb-1">Evidence</p>
          <p className="text-xs text-app-muted leading-relaxed">{a.evidence_summary}</p>
        </div>
      )}

      {/* Timing */}
      {a.forecast_timing_guidance && (
        <div className="px-5 py-3 border-t border-app-line flex items-start gap-2">
          <Clock className="w-3.5 h-3.5 text-app-muted flex-shrink-0 mt-0.5" />
          <p className="text-xs text-app-muted leading-relaxed">{a.forecast_timing_guidance}</p>
        </div>
      )}

      {/* Actions */}
      {actions.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wide mb-2">Next best actions</p>
          <ol className="space-y-2">
            {actions.map((act, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${priorityColor(act.priority)}`} />
                <div className="min-w-0">
                  <span className="text-xs font-medium text-app-ink">{act.action}</span>
                  {act.rationale && (
                    <p className="text-[11px] text-app-muted mt-0.5">{act.rationale}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line">
          <p className="text-[11px] font-semibold text-app-muted uppercase tracking-wide mb-2">Risks</p>
          <ul className="space-y-2">
            {risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${severityColor(r.severity)}`}>
                  {r.severity}
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-app-ink">{r.risk}</p>
                  {r.mitigation && (
                    <p className="text-[11px] text-app-muted mt-0.5">{r.mitigation}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Context gaps */}
      {gaps.length > 0 && (
        <div className="px-5 py-3 border-t border-app-line bg-amber-50/40">
          <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1">What MAVIS doesn't know</p>
          <ul className="space-y-0.5">
            {gaps.map((g, i) => (
              <li key={i} className="text-xs text-amber-700">• {g}</li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
}

// Report a failed MAVIS call and turn it into the message shown to the user.
function toUserError(err: unknown): unknown {
  if (!(err instanceof MavisApiError)) return err;
  captureEdgeFunctionError('mavis-assess-opportunity', err.status, JSON.stringify(err.body));
  return new Error(typeof err.body?.error === 'string' && err.body.error ? err.body.error : `Server error (${err.status})`);
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function MavisForecastAssessmentPanel({ opportunity, qualification, onForecastApplied }: Props) {
  const [assessments, setAssessments] = useState<MavisForecastAssessmentRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  // 'idle' | 'confirm' | 'applied'
  const [applyState, setApplyState] = useState<'idle' | 'confirm' | 'applied'>('idle');
  const [genError, setGenError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const qualPct = calcQualCompleteness(qualification);

  // Load assessment history on mount
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { assessments: data } = await listForecastAssessments(opportunity.id);
      setAssessments(data as unknown as MavisForecastAssessmentRow[]);
    } catch {
      // Keep the current history; the user can still generate a new assessment.
    } finally {
      setLoadingHistory(false);
    }
  }, [opportunity.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Generate assessment
  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);

    try {
      const data = await assessOpportunity(opportunity.id).catch((err: unknown) => {
        throw toUserError(err);
      });
      if (!data.assessment) throw new Error('No assessment returned. Please try again.');

      // Prepend new row (may not have id if save failed — show warning but continue)
      const newRow: MavisForecastAssessmentRow = {
        ...data.assessment,
        id: data.assessment_id ?? `local-${Date.now()}`,
        org_id: opportunity.org_id,
        account_id: opportunity.account_id,
        opportunity_id: opportunity.id,
        created_by: null,
        model: null,
        prompt_slug: 'mavis-assess-opportunity',
        created_at: data.created_at ?? new Date().toISOString(),
      };
      setAssessments(prev => [newRow, ...prev]);
      setApplyState('idle');

      if (data.warning) {
        setGenError(`Warning: ${data.warning}`);
      }
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Failed to generate assessment. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [opportunity]);

  // Step 1: Show confirmation UI
  const handleApplyClick = useCallback(() => {
    setApplyError(null);
    setApplyState('confirm');
  }, []);

  // Build a concise forecast reasoning paragraph from assessment fields
  const buildForecastReasoning = useCallback((a: MavisForecastAssessmentRow): string => {
    const parts: string[] = [];
    if (a.summary?.trim())                    parts.push(a.summary.trim());
    if (a.evidence_summary?.trim())           parts.push(a.evidence_summary.trim());
    if (a.forecast_timing_guidance?.trim())   parts.push(a.forecast_timing_guidance.trim());
    const topRisks = (Array.isArray(a.risks) ? a.risks : []).slice(0, 2) as { risk: string }[];
    const riskText = topRisks.map(r => r.risk).filter(Boolean).join('; ');
    if (riskText) parts.push(`Key risks: ${riskText}.`);
    const result = parts.join(' ');
    return result.length > 700 ? result.slice(0, 697) + '…' : result;
  }, []);

  // Step 2: Execute the write after explicit confirmation
  const handleConfirmApply = useCallback(async () => {
    const latest = assessments[0];
    if (!latest) return;

    setApplying(true);
    setApplyError(null);

    const newLabel = latest.forecast_label.toLowerCase().replace(/ /g, '_');
    const forecastReasoning = buildForecastReasoning(latest);
    const patch = {
      forecast_label: newLabel,
      forecast_percentage: latest.forecast_percentage,
      forecast_source: 'mavis',
      forecast_reasoning: forecastReasoning,
      last_forecast_assessed_at: latest.created_at,
    };

    try {
      await updateOpportunity(opportunity.id, patch);
      setApplyState('applied');
      // State-only update in parent — panel already owns the DB write
      onForecastApplied(patch);
    } catch (err) {
      setApplyError(`Failed to apply: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // stay on confirm so user can retry or cancel
    } finally {
      setApplying(false);
    }
  }, [assessments, buildForecastReasoning, onForecastApplied, opportunity.id]);

  const latest = assessments[0] ?? null;
  const history = assessments.slice(1);

  return (
    <div className="space-y-5">

      {/* ── Current opportunity snapshot ── */}
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-app-line flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-app-muted" />
          <h3 className="text-sm font-semibold text-app-ink">Opportunity Snapshot</h3>
        </div>
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-[11px] text-app-muted uppercase tracking-wide mb-1">Stage</p>
            <p className="text-sm font-semibold text-app-ink capitalize">{opportunity.stage.replace(/_/g, ' ')}</p>
          </div>
          <div>
            <p className="text-[11px] text-app-muted uppercase tracking-wide mb-1">Amount</p>
            <p className="text-sm font-semibold text-app-ink">
              {opportunity.amount ? `${opportunity.currency} ${Number(opportunity.amount).toLocaleString()}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-app-muted uppercase tracking-wide mb-1">Target Close</p>
            <p className="text-sm font-semibold text-app-ink">{opportunity.target_close_date ?? '—'}</p>
          </div>
          <div>
            <p className="text-[11px] text-app-muted uppercase tracking-wide mb-1">Current Forecast</p>
            <div className="flex items-center gap-2">
              {opportunity.forecast_label ? (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${labelColor(opportunity.forecast_label.replace(/_/g, ' '))}`}>
                  {opportunity.forecast_label.replace(/_/g, ' ')}
                </span>
              ) : <span className="text-sm text-app-muted">—</span>}
              {opportunity.forecast_percentage !== null && (
                <span className="text-sm font-bold text-app-ink">{opportunity.forecast_percentage}%</span>
              )}
            </div>
          </div>
        </div>

        {/* Qual completeness bar */}
        <div className="px-6 pb-4 flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-app-muted flex-shrink-0">
            <Target className="w-3 h-3" />
            Qualification completeness
          </div>
          <div className="flex-1 bg-app-canvas rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${qualPct >= 70 ? 'bg-forest-500' : qualPct >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
              style={{ width: `${qualPct}%` }}
            />
          </div>
          <span className="text-[11px] font-semibold text-app-muted flex-shrink-0">{qualPct}%</span>
        </div>
      </div>

      {/* ── Generate button ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-app-muted" />
          <h3 className="text-sm font-semibold text-app-ink">MAVIS Assessment</h3>
          {latest && (
            <span className="text-[11px] text-app-muted">
              Last run {new Date(latest.created_at).toLocaleDateString([], { dateStyle: 'medium' })}
            </span>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-app-surface bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors shadow-sm"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" />Assessing…</>
          ) : (
            <><RefreshCw className="w-4 h-4" />{latest ? 'Re-assess' : 'Assess Opportunity'}</>
          )}
        </button>
      </div>

      {/* Generation error */}
      {genError && (
        <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{genError}</p>
        </div>
      )}

      {/* Apply error */}
      {applyError && (
        <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{applyError}</p>
        </div>
      )}

      {/* Empty state */}
      {!loadingHistory && assessments.length === 0 && !generating && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
          <div className="py-16 text-center px-6">
            <BarChart3 className="w-10 h-10 text-app-muted mx-auto mb-4" />
            <p className="text-sm font-medium text-app-muted mb-1">No assessments yet</p>
            <p className="text-xs text-app-muted max-w-sm mx-auto mb-5">
              Click "Assess Opportunity" to let MAVIS evaluate deal health, forecast probability, risks, and next best actions.
            </p>
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-app-surface bg-forest-600 hover:bg-forest-700 rounded-xl transition-colors shadow-sm"
            >
              <Sparkles className="w-4 h-4" />
              Assess Opportunity
            </button>
          </div>
        </div>
      )}

      {/* Generating skeleton */}
      {generating && (
        <div className="bg-app-surface rounded-2xl border border-forest-200 shadow-sm p-6 animate-pulse flex items-center gap-4">
          <Loader2 className="w-5 h-5 text-forest-400 animate-spin flex-shrink-0" />
          <div className="flex-1">
            <div className="h-3 bg-forest-100 rounded w-1/3 mb-2" />
            <div className="h-2 bg-app-canvas rounded w-2/3" />
          </div>
        </div>
      )}

      {/* Latest assessment */}
      {latest && (
        <AssessmentCard a={latest} isLatest />
      )}

      {/* ── Apply forecast recommendation ── */}
      {latest && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          {applyState === 'applied' ? (
            <div className="px-5 py-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-forest-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-forest-700">Forecast updated</p>
                <p className="text-xs text-app-muted mt-0.5">
                  Applied <strong>{latest.forecast_label}</strong> at <strong>{latest.forecast_percentage}%</strong>.
                  Header, forecast reasoning, and Assessment snapshot updated.
                </p>
              </div>
            </div>
          ) : applyState === 'confirm' ? (
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-app-ink mb-1">Confirm forecast update</p>
                  <p className="text-xs text-app-muted leading-relaxed">
                    This will update the opportunity forecast from{' '}
                    <span className="font-semibold text-app-ink">
                      {opportunity.forecast_label
                        ? `${opportunity.forecast_label.replace(/_/g, ' ')} / ${opportunity.forecast_percentage ?? 0}%`
                        : 'not set'}
                    </span>
                    {' '}to{' '}
                    <span className={`font-semibold px-1.5 py-0.5 rounded border text-[11px] ${labelColor(latest.forecast_label)}`}>
                      {latest.forecast_label}
                    </span>
                    {' / '}
                    <span className="font-semibold text-app-ink">{latest.forecast_percentage}%</span>
                    {' '}and refresh the <strong>forecast reasoning</strong> in Overview with the assessment summary, evidence, timing guidance, and key risks.
                  </p>
                  <p className="text-[11px] text-app-muted mt-1.5">
                    Stage, amount, close date, and owner will not change.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleConfirmApply}
                  disabled={applying}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-app-surface bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
                >
                  {applying
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Applying…</>
                    : <><Check className="w-3.5 h-3.5" />Confirm apply forecast</>
                  }
                </button>
                <button
                  onClick={() => { setApplyState('idle'); setApplyError(null); }}
                  disabled={applying}
                  className="px-4 py-2 text-sm font-medium text-app-muted bg-app-surface border border-app-line hover:bg-app-canvas rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="px-5 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <TrendingUp className="w-4 h-4 text-app-muted flex-shrink-0" />
                <p className="text-xs text-app-muted">
                  Recommendation:{' '}
                  <span className={`font-semibold px-1.5 py-0.5 rounded border text-[11px] ${labelColor(latest.forecast_label)}`}>
                    {latest.forecast_label}
                  </span>
                  {' '}
                  <span className="font-semibold text-app-ink">{latest.forecast_percentage}%</span>
                  {opportunity.forecast_label && (
                    <span className="text-app-muted">
                      {' '}(currently {opportunity.forecast_label.replace(/_/g, ' ')} / {opportunity.forecast_percentage ?? 0}%)
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={handleApplyClick}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 hover:bg-forest-100 border border-forest-200 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowRight className="w-3 h-3" />
                Apply forecast recommendation
              </button>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[11px] text-app-muted font-medium uppercase tracking-wide">
            <ChevronRight className="w-3 h-3" />
            Prior assessments
          </div>
          {history.map((a) => (
            <AssessmentCard key={a.id} a={a} isLatest={false} />
          ))}

        </div>
      )}
    </div>
  );
}
